import { useParams } from 'react-router-dom';
import { QueryClient, queryOptions, useQuery } from '@tanstack/react-query';

import { AndiApi, isNotFound, useAndiAPI } from '../api/andiClient';
import { CHAT_STALE_TIME_MS } from '../constants';

export const chatQueryKey = (projectId: string | undefined, chatId: string) => ['getAndiChat', projectId, chatId] as const;

/** A chat's state, shared by the page, the chat's history load and the chat list's hover prefetch. Nothing
 *  polls it: it is refreshed as a reply streams in and when the reply ends (see `refreshChat`). */
export const getChatQueryObject = (
  projectId: string | undefined,
  chatId: string,
  getAPI: () => Promise<AndiApi>,
) => queryOptions({
  queryKey: chatQueryKey(projectId, chatId),
  queryFn: async () => (await getAPI()).getChat(chatId),
  staleTime: CHAT_STALE_TIME_MS,
});

export const refreshChat = (queryClient: QueryClient, projectId: string | undefined, chatId: string) => (
  queryClient.invalidateQueries({ queryKey: chatQueryKey(projectId, chatId) })
);

export const useChatQuery = (chatId: string | undefined) => {
  const getAPI = useAndiAPI();
  const { projectId } = useParams();

  return useQuery({
    ...getChatQueryObject(projectId, chatId ?? '', getAPI),
    enabled: Boolean(chatId),
    // A deleted chat is gone, not a blip.
    retry: (failures, error) => !isNotFound(error) && failures < 2,
  });
};
